import { athleteCommandCenterData } from "../../app/mock/athleteCommandCenterData";
import { Card } from "../../components/ui/Card";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { useAthleteProfile } from "../athlete-profile/AthleteProfileProvider";
import { createFtpPolicySelection, inferRideSurfaceType, selectFtpForRide, type FtpSelectionResult } from "../athlete-profile/ftpSelection";
import type { RideParserDiagnostics, RideUploadProgress } from "../ride-center/services/rideUploadPipeline";
import type { Ride } from "../ride-center/types/ride";
import type { ActivitySession, ManualSourceLabel } from "./activitySources";
import { ActivitySourceTray, type IntakeConflict, type ActivitySourceTrayDebugState } from "./components/ActivitySourceTray";
import type { CompletedActivityRecord } from "./completedActivityHistory";
import { ActivityHistoryPanel } from "./components/ActivityHistoryPanel";
import { HistoricalRideImportPanel } from "./components/HistoricalRideImportPanel";
import type { HistoricalRideImportResult } from "./historicalRideImport";
import type { HistoricalFitWorkerDiagnostics } from "./workers/historicalFitWorkerClient";

type HistoricalImportRuntimeDiagnostics = {
  fileName: string | null;
  fileSizeBytes: number | null;
  workerStartedAt: string | null;
  currentStage: string | null;
  stageStartedAt: string | null;
  lastWorkerMessageAt: string | null;
  workerState: string | null;
  workerActive: boolean;
  cancellationAvailable: boolean;
};

type TrainLandingPageProps = {
  activitySession: ActivitySession;
  ride: Ride | null;
  rideParseError: string | null;
  uploadProgress: RideUploadProgress | null;
  analysisStage: string | null;
  parserDiagnostics: RideParserDiagnostics | null;
  conflict: IntakeConflict | null;
  restoredFromBrowser?: boolean;
  restoreFailureMessage?: string | null;
  analysisAvailable?: boolean;
  analysisStale?: boolean;
  historyRecords?: CompletedActivityRecord[];
  ftpSelection: FtpSelectionResult;
  onFtpSelectionChange: (selection: FtpSelectionResult) => void;
  onOpenAnalysis?: () => void;
  onOpenHistoryAnalysis?: (activityId: string) => void;
  onDeleteHistoryActivity?: (activityId: string) => void;
  historicalImportResult?: HistoricalRideImportResult | null;
  historicalImportInProgress?: boolean;
  historicalImportProgressPercent?: number;
  historicalImportProgressStage?: string | null;
  historicalImportProgressStatus?: string | null;
  historicalImportWorkerDiagnostics?: HistoricalFitWorkerDiagnostics | null;
  historicalImportRuntimeDiagnostics?: HistoricalImportRuntimeDiagnostics | null;
  onHistoricalImportSelectFiles?: (files: FileList | null) => void;
  onCancelHistoricalImport?: () => void;
  historySummary?: {
    ridesTotal: number;
    ridesFirstDate: string | null;
    ridesLatestDate: string | null;
    dailyHealthTotal: number;
    dailyHealthFirstDate: string | null;
    dailyHealthLatestDate: string | null;
    sources: {
      Garmin: number;
      TrainingPeaks: number;
      AppleHealth: number;
      Tymewear: number;
    };
  };
  onClearSavedActivity?: () => void;
  onStartNewActivity?: () => void;
  debugState?: ActivitySourceTrayDebugState;
  onPrimarySelection: (files: FileList | null) => void;
  onCompanionSelection: (files: FileList | null) => void;
  onScreenshotSelection: (files: FileList | null) => void;
  onReplaceSource: (sourceId: string, files: FileList | null) => void;
  onRemoveSource: (sourceId: string) => void;
  onSourceLabel: (sourceId: string, label: ManualSourceLabel) => void;
  onClearActivity: () => void;
  onRunAnalysis: () => void;
  onResolveConflict: (choice: "create" | "replace" | "cancel") => void;
};

export function TrainLandingPage({
  activitySession,
  ride,
  rideParseError,
  uploadProgress,
  analysisStage,
  parserDiagnostics,
  conflict,
  restoredFromBrowser = false,
  restoreFailureMessage,
  analysisAvailable = false,
  analysisStale = false,
  historyRecords = [],
  ftpSelection,
  onFtpSelectionChange,
  onOpenAnalysis,
  onOpenHistoryAnalysis,
  onDeleteHistoryActivity,
  historicalImportResult,
  historicalImportInProgress = false,
  historicalImportProgressPercent = 0,
  historicalImportProgressStage = null,
  historicalImportProgressStatus = null,
  historicalImportWorkerDiagnostics = null,
  historicalImportRuntimeDiagnostics = null,
  onHistoricalImportSelectFiles,
  onCancelHistoricalImport,
  historySummary,
  onClearSavedActivity,
  onStartNewActivity,
  debugState,
  onPrimarySelection,
  onCompanionSelection,
  onScreenshotSelection,
  onReplaceSource,
  onRemoveSource,
  onSourceLabel,
  onClearActivity,
  onRunAnalysis,
  onResolveConflict
}: TrainLandingPageProps) {
  const { athleteProfile } = useAthleteProfile();
  const rideSurfaceType = inferRideSurfaceType(ride?.sourceFileName ?? activitySession.primary?.file.name ?? "");

  function updatePolicy(kind: FtpSelectionResult["selectedPolicy"]["kind"], manualFtpWatts: number | null = null) {
    const nextSelection = selectFtpForRide(athleteProfile, rideSurfaceType, createFtpPolicySelection(kind, manualFtpWatts));
    onFtpSelectionChange(nextSelection);
  }

  const manualFtpWatts = ftpSelection.selectedPolicy.kind === "manual"
    ? ftpSelection.selectedPolicy.manualFtpWatts ?? athleteProfile.performance.ftpWatts
    : ftpSelection.ftpWatts;

  const resolvedHistorySummary = historySummary ?? {
    ridesTotal: historyRecords.length,
    ridesFirstDate: historyRecords.length > 0 ? historyRecords[historyRecords.length - 1].activityDate : null,
    ridesLatestDate: historyRecords.length > 0 ? historyRecords[0].activityDate : null,
    dailyHealthTotal: 0,
    dailyHealthFirstDate: null,
    dailyHealthLatestDate: null,
    sources: {
      Garmin: 0,
      TrainingPeaks: 0,
      AppleHealth: 0,
      Tymewear: 0
    }
  };

  return (
    <div className="train-layout intake-layout">
      <Card>
        <SectionHeader
          eyebrow="FTP CONTROL"
          title="Ride-level FTP override"
          subtitle="Choose how this ride should resolve FTP"
        />
        <div className="athlete-form-grid">
          <label>FTP policy
            <select value={ftpSelection.selectedPolicy.kind} onChange={event => {
              const kind = event.target.value as FtpSelectionResult["selectedPolicy"]["kind"];
              updatePolicy(kind, kind === "manual" ? ftpSelection.ftpWatts : null);
            }}>
              <option value="athlete-profile-default">Athlete profile default analysis FTP</option>
              <option value="indoor">Indoor FTP</option>
              <option value="outdoor">Outdoor FTP</option>
              <option value="road">Road FTP</option>
              <option value="gravel">Gravel FTP</option>
              <option value="manual">Manual FTP</option>
            </select>
          </label>
          <label>Manual FTP (W)
            <input
              type="number"
              value={manualFtpWatts}
              disabled={ftpSelection.selectedPolicy.kind !== "manual"}
              onChange={event => updatePolicy("manual", Number(event.target.value))}
            />
          </label>
          <label>Resolved FTP<input value={`${ftpSelection.ftpWatts} W`} readOnly /></label>
          <label>Source<input value={ftpSelection.sourceLabel} readOnly /></label>
          <label>Effective date<input value={ftpSelection.effectiveDateIso ? new Date(ftpSelection.effectiveDateIso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "--"} readOnly /></label>
          <label>Policy path<textarea rows={3} value={ftpSelection.policyResolutionPath.join(" -> ")} readOnly /></label>
        </div>
      </Card>

      <ActivitySourceTray
          activitySession={activitySession}
          ride={ride}
          uploadProgress={uploadProgress}
          analysisStage={analysisStage}
          error={rideParseError}
          diagnostics={parserDiagnostics}
          preferredUnits={athleteProfile.personal.preferredUnits}
          conflict={conflict}
          restoredFromBrowser={restoredFromBrowser}
          restoreFailureMessage={restoreFailureMessage}
          analysisAvailable={analysisAvailable}
          analysisStale={analysisStale}
          onOpenAnalysis={onOpenAnalysis}
          onClearSavedActivity={onClearSavedActivity}
          onStartNewActivity={onStartNewActivity}
          debugState={debugState}
          onPrimarySelection={onPrimarySelection}
          onCompanionSelection={onCompanionSelection}
          onScreenshotSelection={onScreenshotSelection}
          onReplaceSource={onReplaceSource}
          onRemoveSource={onRemoveSource}
          onSourceLabel={onSourceLabel}
          onClear={onClearActivity}
          onRunAnalysis={onRunAnalysis}
          onResolveConflict={onResolveConflict}
        />

      <Card>
        <SectionHeader
          eyebrow="HISTORICAL DATA"
          title="Historical Ride Import"
          subtitle="Batch import prior rides without one-by-one analysis sessions"
        />
        <HistoricalRideImportPanel
          importResult={historicalImportResult ?? null}
          isImporting={historicalImportInProgress}
          progressPercent={historicalImportProgressPercent}
          progressStage={historicalImportProgressStage}
          progressStatus={historicalImportProgressStatus}
          workerDiagnostics={historicalImportWorkerDiagnostics}
          runtimeDiagnostics={historicalImportRuntimeDiagnostics}
          onSelectFiles={files => onHistoricalImportSelectFiles?.(files)}
          onCancelImport={() => onCancelHistoricalImport?.()}
        />
      </Card>

      <Card>
        <SectionHeader
          eyebrow="ACTIVITY HISTORY"
          title="Completed ride history"
          subtitle="Open or manage persisted completed analyses"
        />
        <ActivityHistoryPanel
          records={historyRecords}
          preferredUnits={athleteProfile.personal.preferredUnits}
          onOpenAnalysis={activityId => onOpenHistoryAnalysis?.(activityId)}
          onDeleteActivity={activityId => onDeleteHistoryActivity?.(activityId)}
        />
      </Card>

      <Card>
        <SectionHeader
          eyebrow="DATA MANAGEMENT"
          title="History and source coverage"
          subtitle="Compact local-first history inventory"
        />
        <div className="command-grid metrics-3">
          <article className="summary-item">
            <span>Rides</span>
            <strong>{resolvedHistorySummary.ridesTotal}</strong>
            <small>{resolvedHistorySummary.ridesFirstDate ?? "--"} to {resolvedHistorySummary.ridesLatestDate ?? "--"}</small>
          </article>
          <article className="summary-item">
            <span>Daily Health</span>
            <strong>{resolvedHistorySummary.dailyHealthTotal}</strong>
            <small>{resolvedHistorySummary.dailyHealthFirstDate ?? "--"} to {resolvedHistorySummary.dailyHealthLatestDate ?? "--"}</small>
          </article>
          <article className="summary-item">
            <span>Sources</span>
            <strong>{resolvedHistorySummary.sources.Garmin + resolvedHistorySummary.sources.TrainingPeaks + resolvedHistorySummary.sources.AppleHealth + resolvedHistorySummary.sources.Tymewear}</strong>
            <small>Garmin {resolvedHistorySummary.sources.Garmin} · TrainingPeaks {resolvedHistorySummary.sources.TrainingPeaks} · Apple Health {resolvedHistorySummary.sources.AppleHealth} · Tymewear {resolvedHistorySummary.sources.Tymewear}</small>
          </article>
        </div>
      </Card>

      <Card>
        <SectionHeader
          eyebrow="RECENT ACTIVITIES"
          title="Last training sessions"
          subtitle="Quick context for today's decision"
        />
        <ul className="command-list compact-list">
          {athleteCommandCenterData.train.recentActivities.map(activity => (
            <li key={activity}>{activity}</li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionHeader
          eyebrow="WORKOUT LIBRARY"
          title="Ready-to-run sessions"
          subtitle="Curated sessions for current block"
        />
        <ul className="command-list compact-list">
          {athleteCommandCenterData.train.workoutLibrary.map(workout => (
            <li key={workout}>{workout}</li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionHeader
          eyebrow="PERFORMANCE ANALYSIS"
          title="Current analysis priorities"
          subtitle="Insights expanded as data quality increases"
        />
        <ul className="command-list compact-list">
          {athleteCommandCenterData.train.performanceAnalysis.map(item => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Card>

    </div>
  );
}
